import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Statistic,
  Tag,
  Typography,
  Button,
  Spin,
  message,
  Space,
  Popconfirm,
  Modal,
  Tooltip,
  Badge
} from 'antd';
import {
  SyncOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  recurringVendorBillService,
  type RecurringVendorBill,
  type RecurringVendorBillStats,
  type RecurringFrequency
} from '@/services/vendorBillService';
import RecurringVendorBillForm from './RecurringVendorBillForm';

const { Title, Text } = Typography;

const frequencyLabels: Record<RecurringFrequency, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly'
};

const frequencyColors: Record<RecurringFrequency, string> = {
  DAILY: 'cyan',
  WEEKLY: 'blue',
  MONTHLY: 'green',
  QUARTERLY: 'orange',
  YEARLY: 'purple'
};

const RecurringVendorBillManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RecurringVendorBill[]>([]);
  const [stats, setStats] = useState<RecurringVendorBillStats | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RecurringVendorBill | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billsResult, statsResult] = await Promise.all([
        recurringVendorBillService.getAll(),
        recurringVendorBillService.getStats()
      ]);
      setData(billsResult);
      setStats(statsResult);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch recurring bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleCreate = () => {
    setEditingRecord(null);
    setFormVisible(true);
  };

  const handleEdit = (record: RecurringVendorBill) => {
    setEditingRecord(record);
    setFormVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await recurringVendorBillService.delete(id);
      message.success('Recurring bill deleted successfully');
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete recurring bill');
    }
  };

  const handleToggleActive = async (record: RecurringVendorBill) => {
    try {
      if (record.isActive) {
        await recurringVendorBillService.deactivate(record.id);
        message.success('Recurring bill deactivated');
      } else {
        await recurringVendorBillService.activate(record.id);
        message.success('Recurring bill activated');
      }
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to update recurring bill');
    }
  };

  const handleGenerateBill = async (record: RecurringVendorBill) => {
    setProcessingId(record.id);
    try {
      const result = await recurringVendorBillService.generateBills(record.id);
      if (result.generatedBills && result.generatedBills.length > 0) {
        message.success(`Generated ${result.generatedBills.length} bill(s) successfully`);
      } else {
        message.info('No bills were generated (may not be due yet)');
      }
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate bill');
    } finally {
      setProcessingId(null);
    }
  };

  const handleProcessAllDue = async () => {
    setLoading(true);
    try {
      const result = await recurringVendorBillService.processAllDue();
      if (result.processedCount > 0) {
        message.success(`Processed ${result.processedCount} recurring bill(s), generated ${result.generatedBills.length} bill(s)`);
      } else {
        message.info('No recurring bills are due for processing');
      }
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to process due bills');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingRecord(null);
    fetchData();
  };

  const columns: ColumnsType<RecurringVendorBill> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      render: (value, record) => (
        <div>
          <Text strong>{value}</Text>
          {!record.isActive && (
            <Tag color="default" style={{ marginLeft: 8 }}>Inactive</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Vendor',
      key: 'vendor',
      width: 150,
      render: (_, record) => (
        <div>
          <Text>{record.vendor?.name || '-'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.vendor?.code}</Text>
        </div>
      )
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 100,
      render: (value: RecurringFrequency) => (
        <Tag color={frequencyColors[value]}>
          {frequencyLabels[value]}
        </Tag>
      ),
      filters: Object.entries(frequencyLabels).map(([value, label]) => ({
        text: label,
        value
      })),
      onFilter: (value, record) => record.frequency === value
    },
    {
      title: 'Amount',
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      align: 'right',
      width: 120,
      render: (value) => <Text strong>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.amountTotal - b.amountTotal
    },
    {
      title: 'Next Run',
      dataIndex: 'nextRunDate',
      key: 'nextRunDate',
      width: 120,
      render: (value, record) => {
        if (!record.isActive) return <Text type="secondary">-</Text>;
        const nextRun = dayjs(value);
        const isOverdue = nextRun.isBefore(dayjs());
        return (
          <Tooltip title={nextRun.format('YYYY-MM-DD HH:mm')}>
            <Text style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
              {isOverdue ? 'Overdue' : nextRun.format('MMM D, YYYY')}
            </Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => dayjs(a.nextRunDate).unix() - dayjs(b.nextRunDate).unix()
    },
    {
      title: 'Generated',
      dataIndex: 'generatedCount',
      key: 'generatedCount',
      width: 90,
      align: 'center',
      render: (value, record) => (
        <Tooltip title={record.lastGeneratedDate ? `Last: ${dayjs(record.lastGeneratedDate).format('MMM D, YYYY')}` : 'Never generated'}>
          <Badge count={value} showZero color={value > 0 ? 'blue' : 'default'} />
        </Tooltip>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (_, record) => (
        record.isActive ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">Inactive</Tag>
        )
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<ThunderboltOutlined />}
            loading={processingId === record.id}
            disabled={!record.isActive}
            onClick={() => handleGenerateBill(record)}
          >
            Generate
          </Button>
          <Button
            type="default"
            size="small"
            icon={record.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleActive(record)}
          >
            {record.isActive ? 'Pause' : 'Activate'}
          </Button>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this recurring bill?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Spin spinning={loading}>
      <div>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <SyncOutlined style={{ marginRight: 8 }} />
              Recurring Vendor Bills
            </Title>
            <Text type="secondary">
              Manage recurring bill templates for automatic bill generation
            </Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                Refresh
              </Button>
              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleProcessAllDue}
              >
                Process All Due
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Recurring Bill
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Stats Cards */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={4}>
              <Card size="small">
                <Statistic
                  title="Total Templates"
                  value={stats.total}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Card size="small">
                <Statistic
                  title="Active"
                  value={stats.active}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a', fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Card size="small">
                <Statistic
                  title="Inactive"
                  value={stats.inactive}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#8c8c8c', fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Card size="small">
                <Statistic
                  title="Due This Week"
                  value={stats.dueThisWeek}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: stats.dueThisWeek > 0 ? '#faad14' : undefined, fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Card size="small">
                <Statistic
                  title="Due This Month"
                  value={stats.dueThisMonth}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Table */}
        <Card>
          <Table
            dataSource={data}
            columns={columns}
            rowKey="id"
            scroll={{ x: 1300 }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} recurring bills` }}
          />
        </Card>

        {/* Form Modal */}
        <Modal
          title={editingRecord ? 'Edit Recurring Bill' : 'Create Recurring Bill'}
          open={formVisible}
          onCancel={() => {
            setFormVisible(false);
            setEditingRecord(null);
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <RecurringVendorBillForm
            initialValues={editingRecord}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setFormVisible(false);
              setEditingRecord(null);
            }}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default RecurringVendorBillManagement;